import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import CircleContainerizationView from "components/shared/CircleContainerizationView";
import PageV2 from "components/shared/PageV2";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useParams, useSearchParams } from "react-router-dom";
import ForecastProductView from "../ForecastProductView";
import ChooseSubmissionView from "./ChooseSubmissionView";
import { FETCH_SALES_FORECAST_SUBMISSION } from "./graphql";

function parseSubmissionData(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeProductId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
  return raw;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const SubmissionDetailPage = () => {
  const { submissionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({
    searchText: "",
    compareSubmissionId: "",
  });
  const params = Object.fromEntries(searchParams.entries());
  const searchText = params.searchText || "";
  const { loading, error, data } = useQuery(FETCH_SALES_FORECAST_SUBMISSION, {
    variables: { submissionId },
  });

  const compareSubmissionId = params.compareSubmissionId || "";
  const fetchCompareSubmissionQuery = useQuery(
    FETCH_SALES_FORECAST_SUBMISSION,
    {
      variables: { submissionId: compareSubmissionId },
      skip: !compareSubmissionId,
    },
  );

  const modal = useModals();

  const submission = data?.salesForecastSubmission;
  const submissionToCompare = compareSubmissionId
    ? fetchCompareSubmissionQuery.data?.salesForecastSubmission
    : null;

  const parsed = parseSubmissionData(submission?.data);
  const comparisonData = parseSubmissionData(submissionToCompare?.data);
  const comparisonItems = Array.isArray(comparisonData?.items)
    ? comparisonData.items
    : [];
  const comparisonItemsByProductId = comparisonItems.reduce((acc, item) => {
    acc[normalizeProductId(item?.wis_product_id)] = item;
    return acc;
  }, {});
  const months = (Array.isArray(parsed?.columns) ? parsed.columns : [])
    .map((month) => String(month || "").trim())
    .filter(Boolean);
  const products = Array.isArray(submission?.products)
    ? submission.products
    : [];
  const comparisonProducts = Array.isArray(submissionToCompare?.products)
    ? submissionToCompare.products
    : [];
  const productsById = [...comparisonProducts, ...products].reduce(
    (acc, product) => {
      acc[normalizeProductId(product.id)] = product;
      return acc;
    },
    {},
  );
  const items = Array.isArray(parsed?.items) ? parsed.items : [];
  const currentProductIds = new Set(
    items.map((item) => normalizeProductId(item?.wis_product_id)),
  );
  const allItems = [
    ...items,
    ...comparisonItems
      .filter(
        (item) =>
          !currentProductIds.has(normalizeProductId(item?.wis_product_id)),
      )
      .map((item) => ({ ...item, fcst: {} })),
  ];
  const rows = allItems
    .map((item, index) => {
      const productId = normalizeProductId(item?.wis_product_id);
      const fcst = item?.fcst || {};
      const previousFcst = comparisonItemsByProductId[productId]?.fcst || {};
      const hasAnyForecastQty = months.some(
        (month) =>
          toNumber(fcst?.[month]) > 0 || toNumber(previousFcst?.[month]) > 0,
      );
      if (!hasAnyForecastQty) return null;
      return {
        key: `${productId || "unknown"}-${index}`,
        productId,
        product: productsById[productId],
        fcst,
        previousFcst,
      };
    })
    .filter(Boolean);
  const keyword = searchText.trim().toLowerCase();
  const showOnlyDifferences =
    submissionToCompare && params.showOnlyDifferences === "true";
  const filteredRows = rows.filter((row) => {
    if (
      showOnlyDifferences &&
      !months.some(
        (month) =>
          toNumber(row.fcst?.[month]) !== toNumber(row.previousFcst?.[month]),
      )
    ) {
      return false;
    }
    if (!keyword) return true;
    const number = String(row.product?.number || "").toLowerCase();
    const name = String(row.product?.name || "").toLowerCase();
    return number.includes(keyword) || name.includes(keyword);
  });
  const monthCbmTotals = {};
  months.forEach((month) => {
    monthCbmTotals[month] = rows.reduce((sum, row) => {
      const itemsPerSet = Number(row.product?.itemsPerSet) || 0;
      const cbmPerCarton = Number(row.product?.cbm) || 0;
      const qty = toNumber(row.fcst?.[month]);
      if (itemsPerSet <= 0 || cbmPerCarton <= 0 || qty <= 0) return sum;
      const cartonCount = qty / itemsPerSet;
      return sum + cartonCount * cbmPerCarton;
    }, 0);
  });

  const monthsQtyTotals = {};
  months.forEach((month) => {
    monthsQtyTotals[month] = rows.reduce(
      (sum, row) => sum + toNumber(row.fcst?.[month]),
      0,
    );
  });

  const totalCbm = months.reduce(
    (sum, month) => sum + (monthCbmTotals[month] || 0),
    0,
  );
  const shippingZone =
    submission?.forecastCategory?.mode === "INC" ? "US" : "EU";
  const formatCbm = (value) =>
    value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  function showSubmissionSelector() {
    modal.present({
      title: "Compare Submission",
      children: (
        <ChooseSubmissionView
          onSelect={compareSubmission}
          excludeSubmissionId={submission?.id}
          forecastCategoryId={submission?.forecastCategory?.id}
        />
      ),
    });
  }

  function compareSubmission(selectedSubmission) {
    modal.hide();
    setSearchParams({
      ...params,
      compareSubmissionId: selectedSubmission?.id || "",
      showOnlyDifferences: "true",
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <PageV2
      title={`Forecast Submission #${submission.id}`}
      subtitle={`${submission.forecastCategory.name} | ${moment(
        submission.submittedAt,
      ).format(
        "YYYY-MM-DD HH:mm:ss",
      )} by ${submission.user?.email || "Unknown User"}`}
      backTo="./.."
    >
      <div className="h-full flex flex-col gap-4">
        {fetchCompareSubmissionQuery.error && (
          <Errors error={fetchCompareSubmissionQuery.error} />
        )}
        {submissionToCompare && (
          <div className="flex items-center gap-2">
            <span className="font-semibold">Comparing with:</span>
            <span>
              {moment(submissionToCompare.submittedAt).format(
                "YYYY-MM-DD HH:mm:ss",
              )}{" "}
              by {submissionToCompare.user?.email || "Unknown User"}
            </span>

            <Button
              variant="secondary"
              onClick={() =>
                setSearchParams({ ...params, compareSubmissionId: "" })
              }
            >
              Clear Comparison
            </Button>

            <label htmlFor="showOnlyDifferences">
              Show Only Differences
              <input
                id="showOnlyDifferences"
                type="checkbox"
                checked={params.showOnlyDifferences === "true"}
                onChange={(e) =>
                  setSearchParams({
                    ...params,
                    showOnlyDifferences: e.target.checked ? "true" : "false",
                  })
                }
                className="ml-2"
              />
            </label>
          </div>
        )}

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <SearchBar
              value={searchText}
              onChange={(nextSearchText) =>
                setSearchParams({ ...params, searchText: nextSearchText })
              }
              placeholder="Search by product name or SKU"
            />
          </div>

          <div className="flex items-center gap-2">
            {fetchCompareSubmissionQuery.loading ? <Spinner /> : null}
            <Button onClick={showSubmissionSelector}>
              Choose a Submission to Compare
            </Button>
          </div>
        </div>

        <div className="card p-0 flex-1 overflow-auto text-xs">
          <table className="w-full">
            <thead className="sticky top-0 z-20 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80">
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Product ({filteredRows.length})
                </th>
                {months.map((month) => (
                  <th
                    key={month}
                    className="px-2 py-3 text-right whitespace-nowrap border-x border-gray-200 dark:border-gray-700"
                  >
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + months.length}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    {rows.length > 0
                      ? "No products found."
                      : "No submission items."}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-y border-gray-100 dark:border-gray-700"
                  >
                    <td className="px-4 py-2">
                      {row.product ? (
                        <ForecastProductView product={row.product} />
                      ) : (
                        <span className="opacity-70">
                          Unknown product (ID: {row.productId || "N/A"})
                        </span>
                      )}
                    </td>
                    {months.map((month) => {
                      const quantity = toNumber(row.fcst?.[month]);
                      const previousQuantity = toNumber(
                        row.previousFcst?.[month],
                      );
                      const hasChanged =
                        submissionToCompare && quantity !== previousQuantity;
                      const color = hasChanged
                        ? quantity > previousQuantity
                          ? "text-red-500"
                          : "text-green-600"
                        : "";
                      return (
                        <td
                          key={`${row.key}-${month}`}
                          className={`px-2 py-2 text-right whitespace-nowrap border-x border-gray-100 dark:border-gray-700 ${color}`}
                        >
                          {hasChanged
                            ? `${previousQuantity.toLocaleString()} -> ${quantity.toLocaleString()}`
                            : quantity.toLocaleString()}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
            {totalCbm > 0 && (
              <tfoot>
                <tr className="sticky bottom-0 backdrop-blur bg-gray-200 bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90 border-t border-gray-300 dark:border-gray-600 z-20">
                  <td className="px-4 py-3 text-left border-r border-gray-300 dark:border-gray-600">
                    <div>Total CBM ({formatCbm(totalCbm)}m³)</div>
                    <p className="font-normal opacity-70">
                      The total CBM algorithm is trying to find the lowest cost
                      first, then the smallest total container capacity (the
                      highest possible loading rate).
                    </p>
                  </td>
                  {months.map((month) => (
                    <td
                      key={`forecast-cbm-${month}`}
                      className="px-2 py-3 text-right whitespace-nowrap border-x border-gray-300 dark:border-gray-600"
                    >
                      <CircleContainerizationView
                        showCost
                        totalQty={monthsQtyTotals[month] || 0}
                        totalCbm={monthCbmTotals[month] || 0}
                        shippingZone={shippingZone}
                        defaultDisabledContainers={["40GP"]}
                        displayTotalCbm={formatCbm(monthCbmTotals[month] || 0)}
                        className="text-right"
                      />
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </PageV2>
  );
};

export default SubmissionDetailPage;
